/**
 * Custom GPT List Page
 * Page for viewing and managing all Custom GPTs
 */

import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '../../components/ui/button'
import { Input } from '../../components/ui/input'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card'
import { useCustomGPTs } from '../../components/custom-gpt/hooks/useCustomGPTs'
import { deleteCustomGPT } from '../../lib/api/customGPT'
import { CustomGPTNavbar } from '../../components/custom-gpt/CustomGPTNavbar'
import { toast } from 'sonner'
import { ArrowLeft, Plus, Bot, MessageSquare, Edit, Trash2, Globe, Lock, Loader2, Search, X } from 'lucide-react'
import { LoadingSpinner } from '../../components/shared/LoadingSpinner'
import { API_BASE_URL } from '../../lib/config'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../../components/ui/alert-dialog'

export function CustomGPTListPage() {
  const navigate = useNavigate()
  const [page, setPage] = useState(1)
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [currentUserId, setCurrentUserId] = useState<string | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const { gpts, loading, error, total, totalPages, refetch } = useCustomGPTs(page, 20)

  useEffect(() => {
    // Fetch current user ID
    async function fetchCurrentUserId() {
      try {
        const response = await fetch(`${API_BASE_URL}api/custom-gpts/current-user`, {
          credentials: 'include'
        })
        if (response.ok) {
          const data = await response.json()
          setCurrentUserId(data.user_id)
        }
      } catch (error) {
        console.error('Failed to fetch current user ID:', error)
      }
    }
    fetchCurrentUserId()
  }, [])

  // Filter and sort GPTs - user's GPTs first, then others
  const filteredGPTs = gpts
    .filter(gpt => {
      if (!searchTerm.trim()) return true
      const searchLower = searchTerm.toLowerCase()
      return (
        gpt.name.toLowerCase().includes(searchLower) ||
        (gpt.description && gpt.description.toLowerCase().includes(searchLower))
      )
    })
    .sort((a, b) => {
      // User's own GPTs first
      const aIsOwned = currentUserId && a.user_id === currentUserId
      const bIsOwned = currentUserId && b.user_id === currentUserId
      
      if (aIsOwned && !bIsOwned) return -1
      if (!aIsOwned && bIsOwned) return 1
      
      // Within same group, sort by created_at (most recent first)
      const aDate = a.created_at ? new Date(a.created_at).getTime() : 0
      const bDate = b.created_at ? new Date(b.created_at).getTime() : 0
      return bDate - aDate
    })

  const handleDelete = async (gptId: string) => {
    setDeletingId(gptId)
    try {
      await deleteCustomGPT(gptId)
      toast.success('Playbook deleted successfully!')
      refetch()
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to delete playbook')
    } finally {
      setDeletingId(null)
    }
  }

  if (loading && gpts.length === 0) {
    return (
      <>
        <CustomGPTNavbar />
        <div className="min-h-screen bg-light-surface dark:bg-slate-900 flex items-center justify-center">
          <LoadingSpinner size="lg" message="Loading Custom GPTs..." />
        </div>
      </>
    )
  }

  if (error) {
    return (
      <>
        <CustomGPTNavbar />
        <div className="min-h-screen bg-light-surface dark:bg-slate-900 p-4 sm:p-6 lg:p-8">
          <div className="max-w-6xl mx-auto">
            <Button
              variant="ghost"
              onClick={() => navigate('/chat')}
              className="mb-4"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Chat
            </Button>
            <Card>
              <CardContent className="pt-6">
                <div className="text-center">
                  <p className="text-destructive mb-4">{error}</p>
                  <Button onClick={() => refetch()}>Retry</Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </>
    )
  }

  return (
    <>
      <CustomGPTNavbar />
      <div className="min-h-screen bg-light-surface dark:bg-slate-900 p-4 sm:p-6 lg:p-8">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="mb-6 space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div>
                  <h1 className="text-2xl font-bold text-light-text dark:text-white">
                    Playbooks
                  </h1>
                  <p className="text-sm text-muted-foreground mt-1">
                    {searchTerm ? `${filteredGPTs.length} of ${total}` : total} {total === 1 ? 'Playbook' : 'Playbooks'} {searchTerm ? 'found' : 'total'}
                  </p>
                </div>
              </div>
              <Button
                onClick={() => navigate('/playbooks/new')}
                className="bg-brand-blue hover:bg-brand-blue-hover text-white"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Playbook
              </Button>
            </div>
            
            {/* Search Bar */}
            <div className="relative">
              <Search className="absolute left-3.5 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none z-10" />
              <Input
                type="text"
                placeholder="Search playbooks by name or description..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={searchTerm ? "!pl-12 pr-10" : "!pl-12"}
              />
              {searchTerm && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSearchTerm('')}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 h-6 w-6 p-0 hover:bg-transparent z-10"
                >
                  <X className="w-4 h-4" />
                </Button>
              )}
            </div>
          </div>

          {/* Playbooks Grid */}
          {filteredGPTs.length === 0 && gpts.length > 0 ? (
            <Card>
              <CardContent className="pt-6">
                <div className="text-center py-12">
                  <Search className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
                  <h3 className="text-lg font-semibold mb-2">No playbooks found</h3>
                  <p className="text-muted-foreground mb-6">
                    Try adjusting your search terms
                  </p>
                  <Button
                    variant="outline"
                    onClick={() => setSearchTerm('')}
                  >
                    Clear Search
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : filteredGPTs.length === 0 ? (
            <Card>
              <CardContent className="pt-6">
                <div className="text-center py-12">
                  <Bot className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
                  <h3 className="text-lg font-semibold mb-2">No playbooks yet</h3>
                  <p className="text-muted-foreground mb-6">
                    Create your first playbook to get started
                  </p>
                  <Button
                    onClick={() => navigate('/playbooks/new')}
                    className="bg-brand-blue hover:bg-brand-blue-hover text-white"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Create Your First Playbook
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                {filteredGPTs.map((gpt) => (
                  <Card key={gpt.id} className="hover:shadow-lg transition-shadow">
                    <CardHeader>
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          <Bot className="w-5 h-5 text-brand-blue flex-shrink-0" />
                          <CardTitle className="text-lg break-words line-clamp-2" title={gpt.name}>
                            {gpt.name.length > 50 ? `${gpt.name.substring(0, 50)}...` : gpt.name}
                          </CardTitle>
                        </div>
                        {gpt.is_public === 'true' ? (
                          <div title="Public">
                            <Globe className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                          </div>
                        ) : (
                          <div title="Private">
                            <Lock className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                          </div>
                        )}
                      </div>
                      <CardDescription className="line-clamp-2 min-h-[2.5rem]">
                        {gpt.description || '\u00A0'}
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {/* Stats */}
                        <div className="flex items-center gap-4 text-sm text-muted-foreground">
                          {gpt.files && gpt.files.length > 0 && (
                            <span>{gpt.files.length} {gpt.files.length === 1 ? 'file' : 'files'}</span>
                          )}
                          {gpt.conversation_starters && gpt.conversation_starters.length > 0 && (
                            <span>{gpt.conversation_starters.length} {gpt.conversation_starters.length === 1 ? 'starter' : 'starters'}</span>
                          )}
                        </div>

                        {/* Actions */}
                        <div className="flex gap-2">
                          <Button
                            variant="default"
                            size="sm"
                            className="flex-1 bg-brand-blue hover:bg-brand-blue-hover text-white"
                            onClick={() => navigate(`/playbooks/${gpt.id}/chat`)}
                          >
                            <MessageSquare className="w-4 h-4 mr-2" />
                            Chat
                          </Button>
                          {/* Only show edit button if user owns the GPT (or if GPT is not public) */}
                          {(currentUserId === gpt.user_id || gpt.is_public !== 'true') && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => navigate(`/playbooks/${gpt.id}/edit`)}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                          )}
                          {/* Only show delete button if user owns the GPT */}
                          {currentUserId === gpt.user_id && (
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  disabled={deletingId === gpt.id}
                                  className="text-destructive hover:text-destructive"
                                >
                                  {deletingId === gpt.id ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                  ) : (
                                    <Trash2 className="w-4 h-4" />
                                  )}
                                </Button>
                              </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Are you sure?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  This action cannot be undone. This will permanently delete "{gpt.name}".
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => handleDelete(gpt.id)}
                                  className="bg-destructive hover:bg-destructive/90"
                                >
                                  Delete
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                  >
                    Previous
                  </Button>
                  <span className="text-sm text-muted-foreground">
                    Page {page} of {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                  >
                    Next
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  )
}
